import { useState } from 'react';
import { faBadgeCheck, faThumbtack } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@design-system/components/typography/typography';
import { MentionText } from './MentionText';
import { CommunityId } from '../model/community.types';
import { Post, ReactionType } from '../model/post.types';
import { formatPostDate } from '../lib/formatPostDate';
import { useIsAuthorBlocked } from '../api/useIsAuthorBlocked';
import { PostMedia } from './PostMedia';
import { PostReactions } from './PostReactions';
import { PostFooter } from './PostFooter';
import { PostReplies } from './PostReplies';
import { OwnPostActionsMenu } from './OwnPostActionsMenu';
import { OthersPostActionsMenu } from './OthersPostActionsMenu';

type Props = {
  post: Post;
  communityId: CommunityId;
  influencerId?: string | number;
  canModerate: boolean;
  isDmFeatureEnabled: boolean;
  pinnedCount: number;
  showPinnedStyle?: boolean;
  onToggleReaction: (type: ReactionType) => Promise<unknown> | void;
};

export const PostCard = ({
  post,
  communityId,
  influencerId,
  canModerate,
  isDmFeatureEnabled,
  pinnedCount,
  showPinnedStyle,
  onToggleReaction,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const pinnedStyle = showPinnedStyle ?? post.pinned;
  const isAuthorBlocked = useIsAuthorBlocked(communityId);
  const authorBlocked = isAuthorBlocked(post.author.id);

  return (
    <article
      className={`flex w-full flex-col gap-4 p-4 ${
        pinnedStyle ? 'rounded-[10px] bg-[#EDE5FC]' : ''
      }`}
    >
      {pinnedStyle && (
        <div className="flex items-center gap-2 text-[#2C3548]">
          <FontAwesomeIcon icon={faThumbtack} className="text-[12px]" />
          <Typography
            as="span"
            variant="body"
            size="small"
            fontWeight="medium"
            className="uppercase tracking-[1px] text-[#2C3548]"
          >
            Pinned
          </Typography>
        </div>
      )}
      <header className="flex items-center gap-3">
        <img
          src={post.author.avatarUrl}
          alt={post.author.name}
          className="size-10 shrink-0 rounded-full object-cover"
        />
        <span className="flex min-w-0 flex-col">
          <span className="flex items-center gap-2">
            <Typography
              as="span"
              variant="body"
              size="large"
              fontWeight="bold"
              className="text-black"
            >
              {post.author.name}
            </Typography>
            {post.author.isCreator && (
              <FontAwesomeIcon
                icon={faBadgeCheck}
                className="text-[#AD65F5]"
                data-testid="creator-badge"
              />
            )}
            {authorBlocked && (
              <Typography
                as="span"
                variant="body"
                size="small"
                fontWeight="medium"
                className="text-[#E5322D]"
              >
                Blocked
              </Typography>
            )}
          </span>
          <Typography
            as="span"
            variant="body"
            size="small"
            fontWeight="normal"
            className="text-[#2C3548]"
          >
            {formatPostDate(post.createdAt)}
          </Typography>
        </span>
        {canModerate && (
          <div className="ml-auto shrink-0">
            {post.author.isCreator ? (
              <OwnPostActionsMenu
                post={post}
                communityId={communityId}
                influencerId={influencerId}
                pinnedCount={pinnedCount}
              />
            ) : (
              <OthersPostActionsMenu
                post={post}
                communityId={communityId}
                influencerId={influencerId}
                isDmFeatureEnabled={isDmFeatureEnabled}
                pinnedCount={pinnedCount}
                authorBlocked={authorBlocked}
              />
            )}
          </div>
        )}
      </header>
      <MentionText
        content={post.content}
        variant="body"
        size="large"
        fontWeight="normal"
        className="break-words"
      />
      {post.media.length > 0 && <PostMedia media={post.media} />}
      <PostReactions reactions={post.reactions} onToggle={onToggleReaction} />
      <PostFooter
        commentCount={post.commentCount}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded((expanded) => !expanded)}
      />
      {isExpanded && (
        <PostReplies
          communityId={communityId}
          postId={post.id}
          influencerId={influencerId}
          canModerate={canModerate}
          isDmFeatureEnabled={isDmFeatureEnabled}
          commentCount={post.commentCount}
        />
      )}
    </article>
  );
};
